import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { Badge } from '~/components/ui/badge'
import { cn } from '~/lib/utils'
import { scaleLabel } from '~/components/assessment/labels'
import type { AssessmentResponse } from '~/api/types'

/** 검색 가능한 평가 선택 콤보박스 — 평가명·규모·분류(·업종)를 함께 보여준다. */
export function AssessmentPicker({
    assessments,
    value,
    onChange,
}: {
    assessments: AssessmentResponse[]
    value: string // '' = 미연결
    onChange: (id: string) => void
}) {
    const [open, setOpen] = useState(false)
    const [q, setQ] = useState('')
    const ref = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    // 패널을 body로 portal 해서 모달의 overflow 클리핑을 벗어난다. 트리거 위치 기준 고정 배치.
    const [panelStyle, setPanelStyle] = useState<CSSProperties | null>(null)

    // 트리거 위치/뷰포트 여유 공간으로 패널 위치·높이를 계산한다.
    const place = () => {
        const rect = ref.current?.getBoundingClientRect()
        if (!rect) return
        const gap = 4
        const margin = 8
        const spaceBelow = window.innerHeight - rect.bottom - margin
        const spaceAbove = rect.top - margin
        // 아래 공간이 부족하고 위가 더 넓으면 위로 펼친다.
        const dropUp = spaceBelow < 320 && spaceAbove > spaceBelow
        const maxHeight = Math.max(
            160,
            (dropUp ? spaceAbove : spaceBelow) - gap
        )
        setPanelStyle({
            position: 'fixed',
            left: rect.left,
            width: rect.width,
            maxHeight,
            ...(dropUp
                ? { bottom: window.innerHeight - rect.top + gap }
                : { top: rect.bottom + gap }),
        })
    }

    // 열린 동안 위치 재계산(스크롤/리사이즈 대응) + 바깥 클릭 닫기.
    useLayoutEffect(() => {
        if (!open) return
        place()
        const onScrollOrResize = () => place()
        window.addEventListener('scroll', onScrollOrResize, true)
        window.addEventListener('resize', onScrollOrResize)
        const onDoc = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                !ref.current?.contains(target) &&
                !panelRef.current?.contains(target)
            ) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onDoc)
        return () => {
            window.removeEventListener('scroll', onScrollOrResize, true)
            window.removeEventListener('resize', onScrollOrResize)
            document.removeEventListener('mousedown', onDoc)
        }
    }, [open])

    const selected = assessments.find((a) => a.id === value) ?? null
    const needle = q.trim().toLowerCase()
    const filtered = assessments.filter((a) =>
        `${a.title} ${a.industry ?? ''} ${scaleLabel(a.scale)} ${a.classification ?? ''}`
            .toLowerCase()
            .includes(needle)
    )

    const pick = (id: string) => {
        onChange(id)
        setOpen(false)
        setQ('')
    }

    return (
        <div ref={ref} className='relative'>
            <button
                type='button'
                onClick={() => setOpen((o) => !o)}
                className='border-border bg-card text-foreground hover:border-primary/50 flex h-10 w-full items-center justify-between gap-2 rounded-md border px-3 text-sm transition-colors'>
                {selected ? (
                    <span className='flex min-w-0 items-center gap-1.5'>
                        <span className='truncate font-medium'>
                            {selected.title}
                        </span>
                        <Badge tone='info'>{scaleLabel(selected.scale)}</Badge>
                        {selected.classification ? (
                            <Badge tone='accent'>
                                {selected.classification}
                            </Badge>
                        ) : null}
                    </span>
                ) : (
                    <span className='text-muted-foreground'>(나중에 연결)</span>
                )}
                <ChevronsUpDown className='text-muted-foreground size-4 shrink-0' />
            </button>

            {open && panelStyle
                ? createPortal(
                      <div
                          ref={panelRef}
                          style={panelStyle}
                          className='border-border bg-card z-50 flex flex-col overflow-hidden rounded-md border shadow-lg'>
                          <div className='border-border flex shrink-0 items-center gap-2 border-b px-3'>
                              <Search className='text-muted-foreground size-4 shrink-0' />
                              <input
                                  autoFocus
                                  value={q}
                                  onChange={(e) => setQ(e.target.value)}
                                  placeholder='평가명·업종으로 검색…'
                                  className='text-foreground h-9 flex-1 bg-transparent text-sm outline-none'
                              />
                          </div>
                          <ul className='min-h-0 flex-1 overflow-y-auto p-1'>
                              <li>
                                  <Row
                                      active={value === ''}
                                      onClick={() => pick('')}>
                                      <span className='text-muted-foreground text-sm'>
                                          (나중에 연결)
                                      </span>
                                  </Row>
                              </li>
                              {filtered.map((a) => (
                                  <li key={a.id}>
                                      <Row
                                          active={value === a.id}
                                          onClick={() => pick(a.id)}>
                                          <div className='min-w-0'>
                                              <p className='text-foreground truncate text-sm font-medium'>
                                                  {a.title}
                                              </p>
                                              <div className='mt-1 flex flex-wrap items-center gap-1'>
                                                  <Badge tone='info'>
                                                      {scaleLabel(a.scale)}
                                                  </Badge>
                                                  {a.classification ? (
                                                      <Badge tone='accent'>
                                                          {a.classification}
                                                      </Badge>
                                                  ) : null}
                                                  {a.industry ? (
                                                      <span className='text-muted-foreground max-w-[40ch] truncate text-xs'>
                                                          {a.industry}
                                                      </span>
                                                  ) : null}
                                              </div>
                                          </div>
                                      </Row>
                                  </li>
                              ))}
                              {filtered.length === 0 ? (
                                  <li className='text-muted-foreground px-3 py-4 text-center text-xs'>
                                      검색 결과가 없어요.
                                  </li>
                              ) : null}
                          </ul>
                      </div>,
                      document.body
                  )
                : null}
        </div>
    )
}

function Row({
    active,
    onClick,
    children,
}: {
    active: boolean
    onClick: () => void
    children: React.ReactNode
}) {
    return (
        <button
            type='button'
            onClick={onClick}
            className={cn(
                'flex w-full items-start justify-between gap-2 rounded-md px-3 py-2 text-left transition-colors',
                active ? 'bg-accent' : 'hover:bg-muted/60'
            )}>
            {children}
            {active ? (
                <Check className='text-primary mt-0.5 size-4 shrink-0' />
            ) : null}
        </button>
    )
}
